import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

export function LongformEditor({
  label,
  value,
  setValue,
  warnOnUnsaved = true,
}: {
  label: string;
  value: string;
  setValue: (newValue: string) => Promise<void>;
  warnOnUnsaved?: boolean;
}) {
  const [state, setState] = useState(value);
  const hasUnsavedChangesRef = useRef(false);
  const [isAutosaving, setIsAutosaving] = useState(false);

  // Add this effect to sync state with incoming value changes
  useEffect(() => {
    // Only update local state if there are no unsaved changes
    // This prevents overwriting user's work in progress
    if (!hasUnsavedChangesRef.current) {
      setState(value);
    }
  }, [value]);

  async function onBlur() {
    if (hasUnsavedChangesRef.current) {
      await setValue(state);
      hasUnsavedChangesRef.current = false;
    }
  }

  useEffect(() => {
    const unloadEventListener = (ev: BeforeUnloadEvent) => {
      if (hasUnsavedChangesRef.current) {
        ev.preventDefault();
        return "You have unsaved changes. Are you sure?";
      }
    };
    window.addEventListener("beforeunload", unloadEventListener);
    return () =>
      window.removeEventListener("beforeunload", unloadEventListener);
  }, [warnOnUnsaved]);

  // Auto-save every minute
  useEffect(() => {
    const autoSaveInterval = setInterval(async () => {
      if (hasUnsavedChangesRef.current && state !== value) {
        setIsAutosaving(true);
        await setValue(state);
        hasUnsavedChangesRef.current = false;

        // Reset autosaving state after a short delay to make the indicator visible
        setTimeout(() => setIsAutosaving(false), 1000);
      }
    }, 60 * 1000); // 1 minute

    return () => clearInterval(autoSaveInterval);
  }, [state, value, setValue]);

  return (
    <>
      <div className="flex items-center gap-2">
        <h3>{label}</h3>
        {isAutosaving && (
          <span className="text-sm light:text-gray-500 dark:text-gray-400">
            Autosaving...
          </span>
        )}
      </div>
      <textarea
        className={clsx(
          "min-w-[600px] min-h-[300px] rounded p-1",
          "border light:border-stone-700 dark:bg-stone-900",
          "outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 light:focus:outline-blue-600 dark:focus:outline-blue-400",
        )}
        onBlur={onBlur}
        value={state}
        onChange={(ev) => {
          const newValue = ev.target.value;
          hasUnsavedChangesRef.current = true;
          setState(newValue);
        }}
      />
    </>
  );
}
